import { getServerSession } from 'next-auth';
import { authOptions } from './next-auth/authOptions';
import { getSsrContext } from './ssrRequestContext';

async function getServerAccessToken(): Promise<string | null> {
  try {
    const session = await getServerSession(authOptions);
    return session?.accessToken ?? null;
  } catch {
    return null;
  }
}

function logTokenOnce(token: string): void {
  if (process.env.NODE_ENV === 'production') return;
  const ctx = getSsrContext();
  if (ctx && !ctx.tokenLogged) {
    ctx.tokenLogged = true;
    const prefix = `[${ctx.pageId}] ${ctx.page}`;
    console.info(`${prefix} Bearer ${token}`);
  }
}

export interface FetchWithAuthResult {
  res: Response;
  /** Whether a Bearer token was sent with the request. */
  auth: boolean;
  /** Network time in milliseconds. */
  ms: number;
}

/**
 * Drop-in replacement for `fetch` on the server that automatically adds
 * `Authorization: Bearer <token>` when the user has an active session.
 *
 * If the token is missing / expired (401 from backend) the request is
 * retried once without the header so the page never crashes — it just
 * shows unauthenticated data (e.g. inWatchlist = false).
 */
export async function fetchWithAuth(
  url: string,
  init: RequestInit = {},
): Promise<FetchWithAuthResult> {
  const token = await getServerAccessToken();

  if (token) logTokenOnce(token);

  const withAuth = (t: string | null): RequestInit => ({
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
  });

  const start = performance.now();
  const res = await fetch(url, withAuth(token));
  const ms = performance.now() - start;

  // Token present but backend rejected it — retry anonymously
  if (res.status === 401 && token) {
    const retryStart = performance.now();
    const retryRes = await fetch(url, withAuth(null));
    return { res: retryRes, auth: false, ms: performance.now() - retryStart };
  }

  return { res, auth: !!token, ms };
}
